import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClinicalSummary,
  ClinicalSection,
  CLINICAL_SECTION_LABELS,
  CLINICAL_SECTION_DESCRIPTIONS,
  CLINICAL_SECTION_ORDER
} from '@/types/clinicalSummary';
import {
  FileText,
  CheckCircle2,
  XCircle,
  ChevronDown,
  AlertCircle,
  Sparkles,
  Mic,
  Edit3,
  RefreshCw
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import { QuickDictationRecorder } from './QuickDictationRecorder';
import { RichTextEditor } from './RichTextEditor';
import { apiService } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';
import { DocumentationAssistantModal } from './DocumentationAssistantModal';
import { suggestDocumentationRewrite } from '../../services/codingAssistant';
import type { DocumentationSuggestionDTO } from '../../services/codingAssistant';
import { useFeatureFlag } from '@/stores/organizationFeatureFlagsStore';

interface ClinicalSummaryPanelProps {
  summary: ClinicalSummary | null;
  loading?: boolean;
  error?: string | null;
  appointmentId: string;
  isEncounterMode?: boolean;
  noteStatus?: string | null;
  onSummaryUpdate?: () => void;
  onNotesSaved?: (sectionKey: string, richText: string, plainText: string) => void;
}

/**
 * ClinicalSummaryPanel
 *
 * Displays structured clinical documentation extracted from the transcript
 * Shows sections as expandable cards with captured/not_captured status
 */
export const ClinicalSummaryPanel: React.FC<ClinicalSummaryPanelProps> = ({
  summary,
  loading = false,
  error = null,
  appointmentId,
  isEncounterMode = false,
  noteStatus,
  onSummaryUpdate,
  onNotesSaved
}) => {
  const isSigned = noteStatus === 'signed';
  const { isReadOnly } = useAuth();
  // Staff review the summary read-only: no editing, dictation, rewrite or regeneration
  const readOnly = isReadOnly();
  const canEdit = !isSigned && !readOnly;
  const cptCodingAssistantEnabled = useFeatureFlag('cptCodingAssistantEnabled');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['chiefComplaint', 'historyOfPresentIllness', 'treatment', 'assessment', 'plan']) // Expand important sections by default
  );
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());
  const [editingAISections, setEditingAISections] = useState<Set<string>>(new Set());
  const [dictatingSection, setDictatingSection] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Documentation assistant (only shown on the treatment section)
  const [docAssistantOpen, setDocAssistantOpen] = useState(false);
  const [docAssistantLoading, setDocAssistantLoading] = useState(false);
  const [docSuggestion, setDocSuggestion] = useState<DocumentationSuggestionDTO | null>(null);

  const openDocumentationAssistant = async () => {
    if (!canEdit) return;
    setDocAssistantOpen(true);
    setDocAssistantLoading(true);
    setDocSuggestion(null);
    try {
      const treatmentSection = summary?.treatment;
      const currentText = treatmentSection?.physicianNotes?.plainText ?? '';
      if (!currentText.trim()) {
        toast.error('Nothing to rewrite — the treatment section is empty.');
        setDocAssistantOpen(false);
        return;
      }
      const suggestion = await suggestDocumentationRewrite(
        currentText,
        isEncounterMode ? undefined : appointmentId,
        isEncounterMode ? appointmentId : undefined,
        isEncounterMode,
      );
      setDocSuggestion(suggestion);
    } catch (error) {
      console.error('Documentation assistant failed', error);
      toast.error('Documentation assistant unavailable. Try again later.');
      setDocAssistantOpen(false);
    } finally {
      setDocAssistantLoading(false);
    }
  };

  const acceptDocumentationRewrite = async (finalText: string) => {
    // The assistant only handles plain text; wrap into minimal richText for the
    // editor to preserve paragraph breaks.
    const richText = finalText
      .split(/\n\n+/)
      .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');
    await handleSaveNotes('treatment', richText, finalText);
    toast.success('Treatment note updated. Re-extract codes from the coding assistant to refresh.');
  };

  // Retry/regenerate summary after failure
  const handleRetrySummary = async () => {
    if (readOnly) return;
    setIsRetrying(true);
    try {
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${appointmentId}/summary/regenerate`
        : `/v1/clinical/${appointmentId}/summary/regenerate`;

      await apiService.post(endpoint);
      toast.success('Clinical summary regenerated successfully');
      onSummaryUpdate?.();
    } catch (err) {
      console.error('Failed to regenerate summary:', err);
      toast.error('Failed to regenerate summary. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const toggleEditing = (sectionKey: string) => {
    if (!canEdit) return;
    setEditingSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
        // Auto-expand when editing
        setExpandedSections(prevExpanded => {
          const nextExpanded = new Set(prevExpanded);
          nextExpanded.add(sectionKey);
          return nextExpanded;
        });
      }
      return next;
    });
  };

  const toggleAIEditing = (sectionKey: string) => {
    if (!canEdit) return;
    setEditingAISections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
        // Auto-expand when editing
        setExpandedSections(prevExpanded => {
          const nextExpanded = new Set(prevExpanded);
          nextExpanded.add(sectionKey);
          return nextExpanded;
        });
      }
      return next;
    });
  };

  const handleDictationComplete = async (sectionKey: string, transcription: string) => {
    if (!canEdit) return;
    // Append transcription to existing physician notes or create new ones
    const section = summary?.[sectionKey as keyof ClinicalSummary];
    if (!section) return;

    const existingNotes = section.physicianNotes?.richText || '';
    const newNotes = existingNotes
      ? `${existingNotes}<p><br></p><p>${transcription}</p>`
      : `<p>${transcription}</p>`;

    await handleSaveNotes(sectionKey, newNotes, transcription);

    toast.success('Dictation added successfully');
  };

  const handleSaveNotes = async (sectionKey: string, richText: string, plainText: string) => {
    try {
      // Use encounter endpoint in connected mode, appointment endpoint in standalone mode
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${appointmentId}/summary/${sectionKey}/notes`
        : `/v1/clinical/${appointmentId}/summary/${sectionKey}/notes`;

      await apiService.patch(endpoint, { richText, plainText });
      onNotesSaved?.(sectionKey, richText, plainText);
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
      throw error;
    }
  };

  const handleSaveAIContent = async (sectionKey: string, richText: string, plainText: string) => {
    try {
      // Use encounter endpoint in connected mode, appointment endpoint in standalone mode
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${appointmentId}/summary/${sectionKey}/content`
        : `/v1/clinical/${appointmentId}/summary/${sectionKey}/content`;

      await apiService.patch(endpoint, { richText, plainText });
      toast.success('AI content updated successfully');
      toggleAIEditing(sectionKey); // Close editor after save
      onSummaryUpdate?.();
    } catch (error) {
      console.error('Failed to save AI content:', error);
      toast.error('Failed to save AI content');
      throw error;
    }
  };

  // Helper function to render content with highlights
  const renderHighlightedContent = (content: string, section: ClinicalSection, sectionKey: string) => {
    if (!section.contentHighlights || section.contentHighlights.highlights.length === 0) {
      // No highlights - render plain content
      return <p className="text-gray-700 leading-relaxed whitespace-pre-wrap prose prose-sm max-w-none">{content}</p>;
    }

    // Sort highlights by startIndex for rendering
    const sortedHighlights = [...section.contentHighlights.highlights].sort((a, b) => a.startIndex - b.startIndex);

    // Build segments with highlights
    const segments: React.ReactNode[] = [];
    let lastIndex = 0;

    sortedHighlights.forEach((highlight, idx) => {
      // Add text before highlight
      if (highlight.startIndex > lastIndex) {
        segments.push(
          <span key={`text-${idx}`}>
            {content.substring(lastIndex, highlight.startIndex)}
          </span>
        );
      }

      // Add highlighted text with category-based styling
      const highlightClass =
        highlight.category === 'clinical' ? 'bg-yellow-100 border-b-2 border-yellow-400 px-1 rounded' :
        highlight.category === 'safety' ? 'bg-red-100 border-b-2 border-red-400 px-1 rounded font-medium' :
        highlight.category === 'behavioral' ? 'bg-orange-100 border-b-2 border-orange-400 px-1 rounded' :
        highlight.category === 'system_relationship' ? 'bg-blue-100 border-b-2 border-blue-400 px-1 rounded' :
        'bg-gray-100 border-b-2 border-gray-400 px-1 rounded';

      // Build tooltip with enrichment recommendations based on type
      let tooltipContent = '';

      // Check highlight type and provide specific recommendations
      const type = highlight.type.toLowerCase();

      // Allergies
      if (type.includes('allergy')) {
        tooltipContent = '📝 Physician should add:\n• Allergy severity (mild/moderate/severe/life-threatening)\n• Reaction (what happened?)\n• Source (who reported this?)';
      }
      // Medications
      else if (type === 'medication') {
        tooltipContent = '📝 Physician should verify:\n• Dosage, frequency, duration\n• Indication (why prescribed?)';
      }
      // Substance use
      else if (type === 'smoking' || type === 'vaping' || type === 'alcohol' || type === 'illicit_drugs') {
        tooltipContent = '📝 Physician should add:\n• Frequency (how often?)\n• Duration (how long?)\n• Quit attempts or current status';
      }
      // Supplements
      else if (type === 'supplements') {
        tooltipContent = '📝 Physician should add:\n• Specific names and dosages\n• Frequency and reason for use';
      }
      // Housing
      else if (type === 'housing_conditions') {
        tooltipContent = '📝 Physician should add:\n• Specific conditions (mold, overcrowding, utilities)\n• Impact on health';
      }
      // Psychiatric (only if in psychiatric section)
      else if (sectionKey === 'psychiatricHistory' && (type.includes('psychiatric') || type.includes('hallucination'))) {
        tooltipContent = '📝 Physician should add:\n• When was this diagnosed?\n• Last episode date?\n• Current treatment status\n• Relationship to current HPI';
      }
      // Past Medical History
      else if (sectionKey === 'pastMedicalHistory') {
        tooltipContent = '📝 Physician should add:\n• Complete diagnosis/condition details\n• When did this occur?\n• Current status or resolution';
      }
      // HPI symptoms/signs
      else if (sectionKey === 'historyOfPresentIllness') {
        tooltipContent = '📝 Physician should verify:\n• Onset and duration\n• Severity and progression\n• Associated symptoms';
      }
      // Treatment section
      else if (sectionKey === 'treatment') {
        if (type === 'medication_administered' || type === 'injection') {
          tooltipContent = '📝 Physician should verify:\n• Exact dosage, route, and administration site\n• Time of administration\n• Patient response/reaction';
        } else if (type === 'procedure_performed' || type === 'wound_care') {
          tooltipContent = '📝 Physician should add:\n• Procedure details and technique\n• Any complications or patient tolerance\n• Post-procedure instructions given';
        } else if (type === 'treatment_refused') {
          tooltipContent = '📝 Physician should document:\n• Reason for refusal\n• Risks/benefits discussed with patient\n• Alternative options offered';
        } else if (type === 'treatment_unperformed') {
          tooltipContent = '📝 Physician should document:\n• External factor preventing treatment\n• Plan for rescheduling or alternative\n• Patient notification';
        }
      }
      // Plan section
      else if (sectionKey === 'plan') {
        if (type === 'imaging_order' || type === 'lab_order') {
          tooltipContent = '📝 Physician should verify:\n• Specific test ordered\n• Clinical indication\n• Urgency (stat, routine, etc.)';
        } else if (type === 'prescription') {
          tooltipContent = '📝 Physician should verify:\n• Medication name, dosage, frequency, duration\n• Indication and special instructions\n• Drug interactions checked';
        } else if (type === 'referral') {
          tooltipContent = '📝 Physician should verify:\n• Specialty/provider type\n• Reason for referral\n• Urgency and patient preferences';
        } else if (type === 'followup') {
          tooltipContent = '📝 Physician should specify:\n• Exact timing (days/weeks/months)\n• Purpose of follow-up\n• What to monitor before return';
        } else if (type === 'wellness_recommendation') {
          tooltipContent = '📝 Physician should ensure:\n• Specific, actionable advice\n• Connection to patient\'s condition\n• Barriers/support discussed';
        } else if (type === 'patient_education') {
          tooltipContent = '📝 Physician should verify:\n• Clear instructions given\n• Patient understanding confirmed\n• Written materials provided if needed';
        }
      }

      // Default message if no specific recommendation
      if (!tooltipContent) {
        tooltipContent = '📝 Physician review recommended';
      }

      segments.push(
        <Tooltip key={`highlight-${idx}`} content={tooltipContent}>
          <span className={`${highlightClass} cursor-pointer select-none transition-all hover:scale-105 active:scale-105 active:bg-opacity-80 inline-block`}>
            {highlight.text}
          </span>
        </Tooltip>
      );

      lastIndex = highlight.endIndex;
    });

    // Add remaining text
    if (lastIndex < content.length) {
      segments.push(
        <span key="text-end">
          {content.substring(lastIndex)}
        </span>
      );
    }

    return <p className="text-gray-700 leading-relaxed whitespace-pre-wrap prose prose-sm max-w-none">{segments}</p>;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <motion.div
          className="flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <div className="text-sm text-gray-600">Generating clinical summary...</div>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <motion.div
        className="bg-red-50/90 backdrop-blur-sm border border-red-200/50 rounded-xl p-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="text-red-600" size={20} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Failed to Generate Summary</h4>
            <p className="text-sm text-red-700 mb-3">{error}</p>
            {!readOnly && (
              <button
                onClick={handleRetrySummary}
                disabled={isRetrying}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg transition-colors"
              >
                <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} />
                {isRetrying ? 'Regenerating...' : 'Retry'}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // No summary state
  if (!summary) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FileText className="mx-auto mb-3 text-gray-400" size={40} />
          {/* Staff cannot generate; the summary appears once the provider does */}
          {readOnly ? (
            <p className="text-sm text-gray-600">No clinical summary yet</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">No clinical summary available</p>
              <button
                onClick={handleRetrySummary}
                disabled={isRetrying}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg transition-colors"
              >
                <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} />
                {isRetrying ? 'Generating...' : 'Generate Summary'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Calculate stats using ordered sections - filter out undefined sections.
  const sections = CLINICAL_SECTION_ORDER
    .map(key => [key, summary[key]] as [keyof ClinicalSummary, ClinicalSection | undefined])
    .filter((entry): entry is [keyof ClinicalSummary, ClinicalSection] => entry[1] !== undefined);
  const capturedCount = sections.filter(([_, section]) => section.status === 'captured').length;
  const totalCount = sections.length;

  return (
    <div className="space-y-2 md:space-y-4">
      {/* Header with stats */}
      <motion.div
        className="bg-gradient-to-br from-blue-50/80 to-indigo-50/80 backdrop-blur-sm border border-blue-200/50 rounded-xl p-3 md:p-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
              <Sparkles className="text-blue-500" size={20} />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Clinical Summary</h4>
              <p className="text-xs text-gray-600 mt-0.5">
                AI-extracted documentation sections
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-white/80">
              {capturedCount}/{totalCount} captured
            </Badge>
            {/* Show regenerate button only when no sections were captured (empty summary) */}
            {capturedCount === 0 && !readOnly && (
              <button
                onClick={handleRetrySummary}
                disabled={isRetrying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-100 disabled:text-blue-400 rounded-lg transition-colors bg-white/80"
                title="Regenerate clinical summary"
              >
                <RefreshCw size={14} className={isRetrying ? 'animate-spin' : ''} />
                {isRetrying ? 'Generating...' : 'Regenerate'}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Section cards */}
      <div className="space-y-2 md:space-y-3">
        {sections.map(([sectionKey, section], index) => {
          const isExpanded = expandedSections.has(sectionKey);
          const isCaptured = section.status === 'captured';
          const label = CLINICAL_SECTION_LABELS[sectionKey];
          const description = CLINICAL_SECTION_DESCRIPTIONS[sectionKey];

          return (
            <motion.div
              key={sectionKey}
              className={`bg-white/95 backdrop-blur-sm border rounded-xl overflow-hidden transition-all duration-200 ${
                isCaptured
                  ? 'border-green-200/50 hover:border-green-300/70'
                  : 'border-gray-200/50 hover:border-gray-300/70'
              }`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              {/* Section header - always visible */}
              <button
                onClick={() => toggleSection(sectionKey)}
                className="w-full px-3 py-3 md:px-4 min-h-[44px] flex items-center justify-between hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 text-left">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCaptured
                        ? 'bg-green-500/10'
                        : 'bg-gray-500/10'
                    }`}
                  >
                    {isCaptured ? (
                      <CheckCircle2 className="text-green-600" size={16} />
                    ) : (
                      <XCircle className="text-gray-400" size={16} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h5 className="font-semibold text-gray-900 text-sm">{label}</h5>
                      {isCaptured && section.confidence && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-green-50/50 text-green-700 border-green-200"
                        >
                          {Math.round(section.confidence * 100)}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {description}
                    </p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-shrink-0 ml-2"
                >
                  <ChevronDown className="text-gray-400" size={18} />
                </motion.div>
              </button>

              {/* Section content - expandable */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pt-2 md:px-4 md:pb-4 border-t border-gray-100">
                      {/* AI-generated content */}
                      {isCaptured && section.content ? (
                        <motion.div
                          className="mb-3"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-gray-500 font-medium">
                                AI-GENERATED
                              </div>
                              {section.contentHighlights && section.contentHighlights.highlights.length > 0 && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                  {section.contentHighlights.highlights.length} highlights
                                </Badge>
                              )}
                            </div>
                            {!editingAISections.has(sectionKey) && canEdit && (
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAIEditing(sectionKey);
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors min-h-[44px]"
                                >
                                  <Edit3 size={12} />
                                  Edit
                                </button>
                              </div>
                            )}
                          </div>
                          {editingAISections.has(sectionKey) ? (
                            <RichTextEditor
                              // Seed from the original summary content, never the
                              // display copy: the folded ROS card merges in Physical
                              // Exam text that must not be written back into ROS.
                              initialValue={`<p>${summary[sectionKey]?.content ?? ''}</p>`}
                              onSave={(richText, plainText) =>
                                handleSaveAIContent(sectionKey, richText, plainText)
                              }
                              placeholder="Edit AI-generated content..."
                              minHeight="150px"
                            />
                          ) : (
                            renderHighlightedContent(section.content, section, sectionKey)
                          )}
                        </motion.div>
                      ) : (
                        <motion.div
                          className="text-sm text-gray-500 italic bg-gray-50/50 rounded-lg p-3 mb-3"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          No information captured for this section in the transcript
                        </motion.div>
                      )}

                      {/* Physician Notes Section */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs text-gray-500 font-medium">
                            PHYSICIAN NOTES
                          </div>
                          {canEdit && (
                          <div className="flex gap-2 flex-wrap">
                            {sectionKey === 'treatment' && section.physicianNotes?.plainText && cptCodingAssistantEnabled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDocumentationAssistant();
                                }}
                                disabled={docAssistantLoading}
                                className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-colors min-h-[44px] disabled:opacity-50"
                                title="Ask the assistant to suggest a rewrite that is friendlier to the CPT extractor"
                              >
                                <Sparkles size={14} />
                                Improve for coding
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDictatingSection(sectionKey);
                              }}
                              className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors min-h-[44px]"
                            >
                              <Mic size={14} />
                              Dictate
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleEditing(sectionKey);
                              }}
                              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors min-h-[44px] ${
                                editingSections.has(sectionKey)
                                  ? 'text-green-600 bg-green-50'
                                  : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <Edit3 size={14} />
                              {editingSections.has(sectionKey) ? 'Editing' : 'Edit'}
                            </button>
                          </div>
                          )}
                        </div>

                        {/* Rich Text Editor */}
                        {editingSections.has(sectionKey) ? (
                          <RichTextEditor
                            initialValue={section.physicianNotes?.richText || ''}
                            onSave={(richText, plainText) =>
                              handleSaveNotes(sectionKey, richText, plainText)
                            }
                            placeholder="Add your notes here..."
                            minHeight="150px"
                          />
                        ) : section.physicianNotes?.richText ? (
                          <div
                            className="prose prose-sm max-w-none text-gray-700 bg-blue-50/30 rounded-lg p-3"
                            dangerouslySetInnerHTML={{
                              __html: section.physicianNotes.richText
                            }}
                          />
                        ) : (
                          <div className="text-sm text-gray-400 italic text-center py-4">
                            No physician notes added yet
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Footer info */}
      <motion.div
        className="text-xs text-gray-500 text-center py-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        Clinical summary generated using AI with temperature=0 for consistency
      </motion.div>

      {/* Quick Dictation Modal */}
      {dictatingSection && (
        <QuickDictationRecorder
          isOpen={true}
          onClose={() => setDictatingSection(null)}
          onTranscriptionComplete={(transcription) =>
            handleDictationComplete(dictatingSection, transcription)
          }
          sectionLabel={CLINICAL_SECTION_LABELS[dictatingSection as keyof ClinicalSummary]}
          appointmentId={appointmentId}
          sectionKey={dictatingSection}
        />
      )}

      {/* Documentation Assistant — suggests a CPT-extractor-friendly rewrite */}
      <DocumentationAssistantModal
        open={docAssistantOpen}
        onClose={() => setDocAssistantOpen(false)}
        loading={docAssistantLoading}
        suggestion={docSuggestion}
        onAccept={acceptDocumentationRewrite}
      />
    </div>
  );
};
